import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { adminAPI, domainAPI, namesiloAPI, adminExtAPI } from '../lib/api';
import { Shield, Globe, Users, Gavel, BarChart3, Loader2, Trash2, Plus, Download, CheckCircle2, XCircle, Settings, TrendingUp, Star, DollarSign, Leaf, Handshake, Upload, Heart, Bug } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

export default function AdminPage() {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [users, setUsers] = useState([]);
  const [domains, setDomains] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDomain, setShowAddDomain] = useState(false);
  const [newDomain, setNewDomain] = useState({ name: '', tld: '.com', price: '', category: 'tech', description: '', is_auction: false, is_featured: false });

  // NameSilo Import State
  const [nsDomains, setNsDomains] = useState([]);
  const [nsLoading, setNsLoading] = useState(false);
  const [nsImporting, setNsImporting] = useState(false);
  const [nsImported, setNsImported] = useState(null);
  const [nsDefaultPrice, setNsDefaultPrice] = useState('8888');
  const [nsAuctionDays, setNsAuctionDays] = useState('30');
  const [nsOverrides, setNsOverrides] = useState({ 'fixmy.com': '88888' });

  useEffect(() => {
    if (authLoading) return;
    if (!user || !isAdmin) { navigate('/login'); return; }
    const fetch = async () => {
      try {
        const [sRes, uRes, dRes, rRes] = await Promise.all([
          adminAPI.stats(), adminAPI.users(), adminAPI.domains(), adminAPI.serviceRequests()
        ]);
        setStats(sRes.data);
        setUsers(uRes.data.users || []);
        setDomains(dRes.data.domains || []);
        setRequests(rRes.data.requests || []);
      } catch {}
      setLoading(false);
    };
    fetch();
  }, [user, isAdmin, navigate, authLoading]);

  const addDomain = async () => {
    if (!newDomain.name || !newDomain.price) { toast.error('Name and price required'); return; }
    try {
      await domainAPI.create({ ...newDomain, price: parseFloat(newDomain.price) });
      toast.success('Domain added!');
      setShowAddDomain(false);
      setNewDomain({ name: '', tld: '.com', price: '', category: 'tech', description: '', is_auction: false, is_featured: false });
      const dRes = await adminAPI.domains();
      setDomains(dRes.data.domains || []);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const deleteDomain = async (id) => {
    try {
      await domainAPI.delete(id);
      setDomains(prev => prev.filter(d => d.id !== id));
      toast.success('Domain deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const fetchNameSiloDomains = async () => {
    setNsLoading(true);
    try {
      const res = await namesiloAPI.listDomains();
      const domainList = res.data.domains || [];
      setNsDomains(domainList);
      toast.success(`Found ${res.data.count} domains from NameSilo`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to fetch NameSilo domains');
    }
    setNsLoading(false);
  };

  const importToAuctions = async () => {
    if (nsDomains.length === 0) { toast.error('No domains to import'); return; }
    setNsImporting(true);
    try {
      const overridesNum = {};
      Object.entries(nsOverrides).forEach(([k, v]) => { if (v) overridesNum[k] = parseFloat(v); });
      const res = await namesiloAPI.importAuctions({
        domains: nsDomains,
        default_price: parseFloat(nsDefaultPrice) || 8888,
        price_overrides: overridesNum,
        auction_days: parseInt(nsAuctionDays) || 30,
      });
      setNsImported(res.data);
      toast.success(`Imported ${res.data.total_imported} domains as auctions!`);
      const dRes = await adminAPI.domains({ limit: 500 });
      setDomains(dRes.data.domains || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    }
    setNsImporting(false);
  };

  const updateOverride = (domain, price) => {
    setNsOverrides(prev => ({ ...prev, [domain]: price }));
  };

  if (loading || authLoading) return <div className="flex justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-[#00E5FF]" /></div>;

  return (
    <div className="px-6 md:px-12 max-w-7xl mx-auto py-12 md:py-20" data-testid="admin-page">
      <div className="flex items-center gap-3 mb-10">
        <Shield className="h-6 w-6 text-[#00E5FF]" />
        <h1 className="text-4xl font-bold text-white font-['Outfit'] tracking-tight">{t('admin.title')}</h1>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        {[
          { icon: Globe, label: 'Total Domains', value: stats.total_domains || 0 },
          { icon: Users, label: 'Total Users', value: stats.total_users || 0 },
          { icon: Gavel, label: 'Total Auctions', value: stats.total_auctions || 0 },
          { icon: BarChart3, label: 'Total Bids', value: stats.total_bids || 0 },
        ].map((s, i) => (
          <div key={i} className="p-5 bg-[#0A0A0A] border border-white/5 rounded-sm">
            <s.icon className="h-5 w-5 text-[#00E5FF] mb-3" />
            <div className="text-2xl font-bold text-white font-['Outfit']">{s.value}</div>
            <div className="text-[10px] text-zinc-600 uppercase tracking-wider mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Quick Admin Links */}
      <div className="flex flex-wrap gap-3 mb-8">
        <Link to="/admin/analytics">
          <Button variant="outline" className="border-zinc-800 text-zinc-400 hover:border-[#00E5FF] hover:text-[#00E5FF] rounded-sm" data-testid="admin-analytics-link">
            <TrendingUp className="h-4 w-4 mr-2" /> Analytics Dashboard
          </Button>
        </Link>
        <Link to="/admin/financial">
          <Button variant="outline" className="border-zinc-800 text-zinc-400 hover:border-green-500 hover:text-green-400 rounded-sm" data-testid="admin-financial-link">
            <DollarSign className="h-4 w-4 mr-2" /> Financial Reports
          </Button>
        </Link>
        <Link to="/admin/settings">
          <Button variant="outline" className="border-zinc-800 text-zinc-400 hover:border-[#00E5FF] hover:text-[#00E5FF] rounded-sm" data-testid="admin-settings-link">
            <Settings className="h-4 w-4 mr-2" /> API Settings
          </Button>
        </Link>
        <Link to="/admin/auctions">
          <Button variant="outline" className="border-zinc-800 text-zinc-400 hover:border-purple-500 hover:text-purple-400 rounded-sm" data-testid="admin-auctions-link">
            <Gavel className="h-4 w-4 mr-2" /> Auction Control
          </Button>
        </Link>
        <Link to="/admin/garden">
          <Button variant="outline" className="border-zinc-800 text-zinc-400 hover:border-[#00FF94] hover:text-[#00FF94] rounded-sm" data-testid="admin-garden-link">
            <Leaf className="h-4 w-4 mr-2" /> Domain Garden
          </Button>
        </Link>
        <Link to="/admin/brokerage">
          <Button variant="outline" className="border-zinc-800 text-zinc-400 hover:border-cyan-500 hover:text-cyan-400 rounded-sm" data-testid="admin-brokerage-link">
            <Handshake className="h-4 w-4 mr-2" /> Brokerage
          </Button>
        </Link>
        <Link to="/admin/bulk-import">
          <Button variant="outline" className="border-zinc-800 text-zinc-400 hover:border-orange-500 hover:text-orange-400 rounded-sm" data-testid="admin-bulk-import-link">
            <Upload className="h-4 w-4 mr-2" /> Bulk Import
          </Button>
        </Link>
        <Link to="/admin/donations">
          <Button variant="outline" className="border-zinc-800 text-zinc-400 hover:border-pink-500 hover:text-pink-400 rounded-sm" data-testid="admin-donations-link">
            <Heart className="h-4 w-4 mr-2" /> Donations
          </Button>
        </Link>
        <Link to="/admin/bug-reports">
          <Button variant="outline" className="border-zinc-800 text-zinc-400 hover:border-red-500 hover:text-red-400 rounded-sm" data-testid="admin-bug-reports-link">
            <Bug className="h-4 w-4 mr-2" /> Bug Reports
          </Button>
        </Link>
        <Button variant="outline" onClick={async () => {
          try { const r = await adminExtAPI.bulkValuate(); toast.success(r.data.message); } catch { toast.error('Valuation failed'); }
        }} className="border-zinc-800 text-zinc-400 hover:border-amber-500 hover:text-amber-400 rounded-sm" data-testid="bulk-valuate-btn">
          <Star className="h-4 w-4 mr-2" /> Run Bulk Valuation
        </Button>
      </div>

      <Tabs defaultValue="domains" className="space-y-6">
        <TabsList className="bg-[#0A0A0A] border border-white/5 p-1 rounded-sm">
          <TabsTrigger value="domains" className="rounded-sm text-xs uppercase tracking-wider data-[state=active]:bg-[#00E5FF] data-[state=active]:text-black" data-testid="admin-tab-domains">{t('admin.manageDomains')}</TabsTrigger>
          <TabsTrigger value="namesilo" className="rounded-sm text-xs uppercase tracking-wider data-[state=active]:bg-[#00E5FF] data-[state=active]:text-black" data-testid="admin-tab-namesilo">NameSilo Import</TabsTrigger>
          <TabsTrigger value="users" className="rounded-sm text-xs uppercase tracking-wider data-[state=active]:bg-[#00E5FF] data-[state=active]:text-black" data-testid="admin-tab-users">{t('admin.manageUsers')}</TabsTrigger>
          <TabsTrigger value="requests" className="rounded-sm text-xs uppercase tracking-wider data-[state=active]:bg-[#00E5FF] data-[state=active]:text-black" data-testid="admin-tab-requests">{t('admin.requests')}</TabsTrigger>
        </TabsList>

        <TabsContent value="domains">
          <div className="flex justify-end mb-4">
            <Button onClick={() => setShowAddDomain(!showAddDomain)} className="bg-[#00E5FF] text-black font-bold rounded-sm" data-testid="add-domain-btn">
              <Plus className="h-4 w-4 mr-1" /> Add Domain
            </Button>
          </div>
          {showAddDomain && (
            <div className="bg-[#0A0A0A] border border-white/5 rounded-sm p-6 mb-6 animate-fade-in" data-testid="add-domain-form">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Domain Name</Label>
                  <Input value={newDomain.name} onChange={(e) => setNewDomain(p => ({ ...p, name: e.target.value }))}
                    className="mt-1 bg-[#050505] border-zinc-800 text-white rounded-sm" placeholder="example" data-testid="new-domain-name" />
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">TLD</Label>
                  <Select value={newDomain.tld} onValueChange={(v) => setNewDomain(p => ({ ...p, tld: v }))}>
                    <SelectTrigger className="mt-1 bg-[#050505] border-zinc-800 text-white rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10">
                      {['.com', '.io', '.ai', '.net', '.org', '.co', '.dev', '.app'].map(t => (
                        <SelectItem key={t} value={t} className="text-zinc-300">{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Price ($)</Label>
                  <Input type="number" value={newDomain.price} onChange={(e) => setNewDomain(p => ({ ...p, price: e.target.value }))}
                    className="mt-1 bg-[#050505] border-zinc-800 text-white rounded-sm" placeholder="10000" data-testid="new-domain-price" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Category</Label>
                  <Select value={newDomain.category} onValueChange={(v) => setNewDomain(p => ({ ...p, category: v }))}>
                    <SelectTrigger className="mt-1 bg-[#050505] border-zinc-800 text-white rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10">
                      {['tech', 'business', 'crypto', 'finance', 'health', 'media', 'gaming', 'ai'].map(c => (
                        <SelectItem key={c} value={c} className="text-zinc-300">{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider">Description</Label>
                  <Input value={newDomain.description} onChange={(e) => setNewDomain(p => ({ ...p, description: e.target.value }))}
                    className="mt-1 bg-[#050505] border-zinc-800 text-white rounded-sm" placeholder="Brief description" />
                </div>
              </div>
              <div className="flex gap-4 mt-4">
                <label className="flex items-center gap-2 text-sm text-zinc-400 cursor-pointer">
                  <input type="checkbox" checked={newDomain.is_featured} onChange={(e) => setNewDomain(p => ({ ...p, is_featured: e.target.checked }))} className="accent-[#00E5FF]" />
                  Featured
                </label>
                <label className="flex items-center gap-2 text-sm text-zinc-400 cursor-pointer">
                  <input type="checkbox" checked={newDomain.is_auction} onChange={(e) => setNewDomain(p => ({ ...p, is_auction: e.target.checked }))} className="accent-[#00E5FF]" />
                  Auction
                </label>
              </div>
              <Button onClick={addDomain} className="mt-4 bg-[#00E5FF] text-black font-bold rounded-sm" data-testid="submit-domain-btn">Add Domain</Button>
            </div>
          )}
          <div className="bg-[#0A0A0A] border border-white/5 rounded-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead><tr className="border-b border-white/5">
                  <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">Domain</th>
                  <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">Price</th>
                  <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">Category</th>
                  <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">Type</th>
                  <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">Status</th>
                  <th className="text-right px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">Actions</th>
                </tr></thead>
                <tbody>
                  {domains.map(d => (
                    <tr key={d.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                      <td className="px-4 py-3 text-sm text-white font-medium">{d.full_name}</td>
                      <td className="px-4 py-3 text-sm text-zinc-400">${d.price?.toLocaleString()}</td>
                      <td className="px-4 py-3 text-xs text-zinc-500 uppercase">{d.category}</td>
                      <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-sm ${d.is_auction ? 'bg-amber-500/10 text-amber-400' : 'bg-[#00E5FF]/10 text-[#00E5FF]'}`}>{d.is_auction ? 'Auction' : 'Buy Now'}</span></td>
                      <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-sm ${d.status === 'active' ? 'bg-green-500/10 text-green-400' : 'bg-zinc-800 text-zinc-500'}`}>{d.status}</span></td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => deleteDomain(d.id)} className="text-red-400 hover:text-red-300" data-testid={`delete-domain-${d.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="namesilo">
          <div className="space-y-6">
            {/* Fetch Domains */}
            <div className="bg-[#0A0A0A] border border-white/5 rounded-sm p-6">
              <h3 className="text-lg font-bold text-white font-['Outfit'] mb-4">Import Domains from NameSilo</h3>
              <p className="text-sm text-zinc-500 mb-4">Fetch your domains from NameSilo and list them as auctions on XDomainX.</p>
              <Button onClick={fetchNameSiloDomains} disabled={nsLoading} className="bg-[#00E5FF] text-black font-bold rounded-sm" data-testid="fetch-namesilo-btn">
                {nsLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
                Fetch from NameSilo
              </Button>
            </div>

            {/* Domain List */}
            {nsDomains.length > 0 && (
              <div className="bg-[#0A0A0A] border border-white/5 rounded-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-white font-['Outfit']">{nsDomains.length} Domains Found</h3>
                </div>

                {/* Import Settings */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 p-4 bg-[#050505] border border-white/5 rounded-sm">
                  <div>
                    <Label className="text-zinc-400 text-xs uppercase tracking-wider">Default Auction Price ($)</Label>
                    <Input value={nsDefaultPrice} onChange={(e) => setNsDefaultPrice(e.target.value)}
                      className="mt-1 bg-[#020202] border-zinc-800 text-white rounded-sm" data-testid="ns-default-price" />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs uppercase tracking-wider">Auction Duration (days)</Label>
                    <Input value={nsAuctionDays} onChange={(e) => setNsAuctionDays(e.target.value)}
                      className="mt-1 bg-[#020202] border-zinc-800 text-white rounded-sm" data-testid="ns-auction-days" />
                  </div>
                  <div className="flex items-end">
                    <Button onClick={importToAuctions} disabled={nsImporting} className="w-full bg-[#00E5FF] text-black font-bold rounded-sm h-10" data-testid="import-auctions-btn">
                      {nsImporting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      Import All as Auctions
                    </Button>
                  </div>
                </div>

                {/* Price Overrides */}
                <div className="mb-4">
                  <Label className="text-zinc-400 text-xs uppercase tracking-wider mb-2 block">Price Overrides</Label>
                  <div className="flex gap-3 items-center p-2 bg-[#050505] border border-white/5 rounded-sm">
                    <span className="text-sm text-amber-400 font-medium">fixmy.com</span>
                    <span className="text-zinc-600">=</span>
                    <Input value={nsOverrides['fixmy.com'] || ''} onChange={(e) => updateOverride('fixmy.com', e.target.value)}
                      className="w-32 bg-[#020202] border-zinc-800 text-white rounded-sm h-8 text-sm" data-testid="ns-override-fixmy" />
                  </div>
                </div>

                {/* Domain List */}
                <div className="max-h-96 overflow-y-auto border border-white/5 rounded-sm">
                  <table className="w-full">
                    <thead className="sticky top-0 bg-[#0A0A0A]"><tr className="border-b border-white/5">
                      <th className="text-left px-4 py-2 text-[10px] text-zinc-600 uppercase tracking-wider">#</th>
                      <th className="text-left px-4 py-2 text-[10px] text-zinc-600 uppercase tracking-wider">Domain</th>
                      <th className="text-left px-4 py-2 text-[10px] text-zinc-600 uppercase tracking-wider">Registered</th>
                      <th className="text-left px-4 py-2 text-[10px] text-zinc-600 uppercase tracking-wider">Expires</th>
                      <th className="text-right px-4 py-2 text-[10px] text-zinc-600 uppercase tracking-wider">Auction Price</th>
                    </tr></thead>
                    <tbody>
                      {nsDomains.map((d, i) => {
                        const domName = typeof d === 'object' ? d.name : d;
                        return (
                          <tr key={domName} className="border-b border-white/5 hover:bg-white/[0.02]">
                            <td className="px-4 py-2 text-xs text-zinc-600">{i + 1}</td>
                            <td className="px-4 py-2 text-sm text-white font-medium">{domName}</td>
                            <td className="px-4 py-2 text-xs text-zinc-500">{d.created || '-'}</td>
                            <td className="px-4 py-2 text-xs text-zinc-500">{d.expires || '-'}</td>
                            <td className="px-4 py-2 text-sm text-right">
                              <span className={`font-bold ${nsOverrides[domName] ? 'text-amber-400' : 'text-[#00E5FF]'}`}>
                                ${Number(nsOverrides[domName] || nsDefaultPrice || '8888').toLocaleString()}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Import Results */}
            {nsImported && (
              <div className="bg-[#0A0A0A] border border-white/5 rounded-sm p-6" data-testid="import-results">
                <h3 className="text-lg font-bold text-white font-['Outfit'] mb-4">Import Results</h3>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="p-4 bg-green-500/5 border border-green-500/20 rounded-sm">
                    <div className="flex items-center gap-2 mb-1"><CheckCircle2 className="h-4 w-4 text-green-400" /><span className="text-sm font-bold text-green-400">Imported</span></div>
                    <div className="text-2xl font-bold text-white font-['Outfit']">{nsImported.total_imported}</div>
                  </div>
                  <div className="p-4 bg-zinc-500/5 border border-zinc-500/20 rounded-sm">
                    <div className="flex items-center gap-2 mb-1"><XCircle className="h-4 w-4 text-zinc-400" /><span className="text-sm font-bold text-zinc-400">Skipped (already exists)</span></div>
                    <div className="text-2xl font-bold text-white font-['Outfit']">{nsImported.total_skipped}</div>
                  </div>
                </div>
                {nsImported.imported.length > 0 && (
                  <div className="max-h-48 overflow-y-auto">
                    {nsImported.imported.slice(0, 20).map((d, i) => (
                      <div key={i} className="flex justify-between py-1.5 border-b border-white/5 text-sm">
                        <span className="text-white">{d.domain}</span>
                        <span className="text-[#00E5FF] font-bold">${d.price.toLocaleString()}</span>
                      </div>
                    ))}
                    {nsImported.imported.length > 20 && <p className="text-xs text-zinc-500 mt-2">...and {nsImported.imported.length - 20} more</p>}
                  </div>
                )}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="users">
          <div className="bg-[#0A0A0A] border border-white/5 rounded-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead><tr className="border-b border-white/5">
                  <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">Name</th>
                  <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">Email</th>
                  <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">Role</th>
                  <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">Joined</th>
                </tr></thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                      <td className="px-4 py-3 text-sm text-white">{u.name}</td>
                      <td className="px-4 py-3 text-sm text-zinc-400">{u.email}</td>
                      <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-sm ${u.role === 'admin' ? 'bg-purple-500/10 text-purple-400' : 'bg-zinc-800 text-zinc-400'}`}>{u.role}</span></td>
                      <td className="px-4 py-3 text-sm text-zinc-500">{u.created_at ? new Date(u.created_at).toLocaleDateString() : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="requests">
          {requests.length === 0 ? (
            <div className="text-center py-16 bg-[#0A0A0A] border border-white/5 rounded-sm"><p className="text-zinc-500">No service requests yet</p></div>
          ) : (
            <div className="bg-[#0A0A0A] border border-white/5 rounded-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead><tr className="border-b border-white/5">
                    <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">Type</th>
                    <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">Name</th>
                    <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">Email</th>
                    <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">Domain</th>
                    <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">Status</th>
                    <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider">Date</th>
                  </tr></thead>
                  <tbody>
                    {requests.map(r => (
                      <tr key={r.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                        <td className="px-4 py-3 text-xs text-[#00E5FF] uppercase">{r.service_type}</td>
                        <td className="px-4 py-3 text-sm text-white">{r.name}</td>
                        <td className="px-4 py-3 text-sm text-zinc-400">{r.email}</td>
                        <td className="px-4 py-3 text-sm text-zinc-400">{r.domain_name || '-'}</td>
                        <td className="px-4 py-3"><span className="text-xs px-2 py-0.5 rounded-sm bg-amber-500/10 text-amber-400">{r.status}</span></td>
                        <td className="px-4 py-3 text-sm text-zinc-500">{new Date(r.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
